import { useEffect } from "react";

/**
 * Scroll-triggered reveal system (Autocorp-style staggered fade-in-up), prerender-safe.
 *
 * Usage: add `reveal` (+ optional `reveal-delay-1..6`) to any element, then call
 * this hook once in the page component. Elements get `.is-revealed` when they
 * enter the viewport. Classes live in src/index.css.
 *
 * Prerender safety: Puppeteer (navigator.webdriver) and prefers-reduced-motion
 * both reveal everything immediately, so prerendered HTML is never blank and
 * motion-sensitive users see static content.
 */
export function useRevealObserver() {
  useEffect(() => {
    const revealAll =
      navigator.webdriver === true ||
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const grab = () =>
      Array.from(document.querySelectorAll<HTMLElement>(".reveal:not(.is-revealed)"));

    if (revealAll) {
      grab().forEach((el) => el.classList.add("is-revealed"));
      // Lazy-mounted sections appear later: reveal those instantly too.
      const mo = new MutationObserver(() => grab().forEach((el) => el.classList.add("is-revealed")));
      mo.observe(document.body, { childList: true, subtree: true });
      return () => mo.disconnect();
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.06 },
    );
    grab().forEach((el) => io.observe(el));

    // Watch for reveal elements added after mount (lazy/deferred sections).
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const els = node.matches?.(".reveal:not(.is-revealed)")
            ? [node]
            : Array.from(node.querySelectorAll?.<HTMLElement>(".reveal:not(.is-revealed)") ?? []);
          els.forEach((el) => io.observe(el));
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
}
